import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, Typography, CircularProgress } from '@mui/material';
import type { PaletteMode } from '@mui/material';
import { createAppTheme } from './ui-theme';
import { ColorModeContext } from './ColorModeContext';
import { AuthProvider, useAuth } from './AuthContext';
import { EnvironmentProvider, useEnvironment } from './EnvironmentContext';
import { TeamAccessProvider } from './TeamAccessContext';
import { AssistantProvider } from './AssistantContext';
import ErrorBoundary from './components/ErrorBoundary';
import FeatureRoute from './components/FeatureRoute';
import { usePageTracking } from './hooks/usePageTracking';
import { lazyPage } from './lazyPage';
import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import ClearanceLayout from './components/ClearanceLayout';
import HelpdeskLayout from './components/HelpdeskLayout';
import ChangeRadarLayout from './components/ChangeRadarLayout';
import MsApproveOperationsLayout from './components/MsApproveOperationsLayout';
import SignInPage from './pages/SignInPage';
import LegacyIpAnchorRedirect from './components/ipAnchor/LegacyIpAnchorRedirect';

const HomeRoute: React.FC = () => {
  const { configurationProfile } = useEnvironment();
  return configurationProfile === 'Default' ? <HomePage /> : <Navigate to="/requests" replace />;
};

/*
 * Route components are code-split. Before this, every page in the application was compiled into a
 * single bundle, so a requestor who only ever submits a request still downloaded Change Radar,
 * Clearance, the operations centre and every admin tool before the first screen could paint. Each
 * page below is now its own chunk, fetched on first navigation to it.
 *
 * The layout components stay eager on purpose: they are small, they render around the page rather
 * than instead of it, and lazy-loading a layout would put two sequential round trips in front of a
 * nested route instead of one. HomePage stays eager because it is the landing route — deferring it
 * would only move the wait rather than remove it.
 * SignInPage is the anonymous landing screen and stays eager for the same first-paint reason.
 */

const MyRequestsPage = lazyPage(() => import('./pages/MyRequestsPage'), 'MyRequestsPage');
const ClaimsPage = lazyPage(() => import('./pages/ClaimsPage'), 'ClaimsPage');
const ClearanceEvaluatePage = lazyPage(() => import('./pages/ClearanceEvaluatePage'), 'ClearanceEvaluatePage');
const ClearanceApplicationsPage = lazyPage(() => import('./pages/ClearanceApplicationsPage'), 'ClearanceApplicationsPage');
const ClearanceServicesPage = lazyPage(() => import('./pages/ClearanceServicesPage'), 'ClearanceServicesPage');
const ClearanceOperationContextsPage = lazyPage(() => import('./pages/ClearanceOperationContextsPage'), 'ClearanceOperationContextsPage');
const ClearanceTenantsPage = lazyPage(() => import('./pages/ClearanceTenantsPage'), 'ClearanceTenantsPage');
const ClearanceVisualizationPage = lazyPage(() => import('./pages/ClearanceVisualizationPage'), 'ClearanceVisualizationPage');
const FicDiagnosticsPage = lazyPage(() => import('./pages/FicDiagnosticsPage'), 'FicDiagnosticsPage');
const SelectRequestTypePage = lazyPage(() => import('./pages/SelectRequestTypePage'), 'SelectRequestTypePage');
const ExceptionCentralPage = lazyPage(() => import('./pages/ExceptionCentralPage'), 'ExceptionCentralPage');
const AttestationCentralPage = lazyPage(() => import('./pages/AttestationCentralPage'), 'AttestationCentralPage');
const UamiAttestationDashboardPage = lazyPage(() => import('./pages/UamiAttestationDashboardPage'), 'UamiAttestationDashboardPage');
const UamiCampaignAdminPage = lazyPage(() => import('./pages/UamiCampaignAdminPage'), 'UamiCampaignAdminPage');
const CustomRoleAttestationDashboardPage = lazyPage(() => import('./pages/CustomRoleAttestationDashboardPage'), 'CustomRoleAttestationDashboardPage');
const AppAttestationMockPage = lazyPage(() => import('./pages/AppAttestationMockPage'), 'AppAttestationMockPage');
const NewRequestPage = lazyPage(() => import('./pages/NewRequestPage'), 'NewRequestPage');
const RequestDetailPage = lazyPage(() => import('./pages/RequestDetailPage'), 'RequestDetailPage');
const RequestSubmittedPage = lazyPage(() => import('./pages/RequestSubmittedPage'), 'RequestSubmittedPage');
const ManageRequestsPage = lazyPage(() => import('./pages/ManageRequestsPage'), 'ManageRequestsPage');
const ManageExceptionsPage = lazyPage(() => import('./pages/ManageExceptionsPage'), 'ManageExceptionsPage');
const BulkUploadExceptionsPage = lazyPage(() => import('./pages/BulkUploadExceptionsPage'), 'BulkUploadExceptionsPage');
const FeedbackBoardPage = lazyPage(() => import('./pages/FeedbackBoardPage'), 'FeedbackBoardPage');
const HarvestManagementPage = lazyPage(() => import('./pages/HarvestManagementPage'), 'HarvestManagementPage');
const OnboardingBuddyPage = lazyPage(() => import('./pages/OnboardingBuddyPage'), 'OnboardingBuddyPage');
const WorkItemOnboardTeamPage = lazyPage(() => import('./pages/WorkItemOnboardTeamPage'), 'WorkItemOnboardTeamPage');
const WorkItemRoutingAdminPage = lazyPage(() => import('./pages/WorkItemRoutingAdminPage'), 'WorkItemRoutingAdminPage');
const CapabilityMatrixAdminPage = lazyPage(() => import('./pages/CapabilityMatrixAdminPage'), 'CapabilityMatrixAdminPage');
const DeflectionCatalogPage = lazyPage(() => import('./pages/DeflectionCatalogPage'), 'DeflectionCatalogPage');
const TaxonomyMappingPage = lazyPage(() => import('./pages/TaxonomyMappingPage'), 'TaxonomyMappingPage');
const WorkItemManageTeamPage = lazyPage(() => import('./pages/WorkItemManageTeamPage'), 'WorkItemManageTeamPage');
const WorkRequestSubmitPage = lazyPage(() => import('./pages/WorkRequestSubmitPage'), 'WorkRequestSubmitPage');
const TeamBacklogPage = lazyPage(() => import('./pages/TeamBacklogPage'), 'TeamBacklogPage');
const WorkRequestDetailPage = lazyPage(() => import('./pages/WorkRequestDetailPage'), 'WorkRequestDetailPage');
const WorkRequestArtifactPage = lazyPage(() => import('./pages/WorkRequestArtifactPage'), 'WorkRequestArtifactPage');
const HelpdeskCafePage = lazyPage(() => import('./pages/HelpdeskCafePage'), 'HelpdeskCafePage');
const HelpdeskRiskPage = lazyPage(() => import('./pages/HelpdeskRiskPage'), 'HelpdeskRiskPage');
const HelpdeskFalconPage = lazyPage(() => import('./pages/HelpdeskFalconPage'), 'HelpdeskFalconPage');
const HelpdeskPhishPage = lazyPage(() => import('./pages/HelpdeskPhishPage'), 'HelpdeskPhishPage');
const HelpdeskIamAgentAssistantPage = lazyPage(
  () => import('./pages/HelpdeskIamAgentAssistantPage'),
  'HelpdeskIamAgentAssistantPage',
);
const WhoAmIPage = lazyPage(() => import('./pages/WhoAmIPage'), 'WhoAmIPage');
const AnalyticsDashboardPage = lazyPage(() => import('./pages/AnalyticsDashboardPage'), 'AnalyticsDashboardPage');
const ExceptionReportPage = lazyPage(() => import('./pages/ExceptionReportPage'), 'ExceptionReportPage');
const GuestPassPage = lazyPage(() => import('./pages/GuestPassPage'), 'GuestPassPage');
const GuestPassNewUiPage = lazyPage(() => import('./pages/GuestPassNewUiPage'), 'GuestPassNewUiPage');
const HrAccountLookupPage = lazyPage(() => import('./pages/HrAccountLookupPage'), 'HrAccountLookupPage');
const CoreIdHrAccountLookupPage = lazyPage(() => import('./pages/CoreIdHrAccountLookupPage'), 'CoreIdHrAccountLookupPage');
const PimSyncManagementPage = lazyPage(() => import('./pages/PimSyncManagementPage'), 'PimSyncManagementPage');
const TotpManagementPage = lazyPage(() => import('./pages/TotpManagementPage'), 'TotpManagementPage');
const ContributorsPage = lazyPage(() => import('./pages/ContributorsPage'), 'ContributorsPage');
const UserToolsPage = lazyPage(() => import('./pages/UserToolsPage'), 'UserToolsPage');
const InactiveUsersPage = lazyPage(() => import('./pages/InactiveUsersPage'), 'InactiveUsersPage');
const IcmReportPage = lazyPage(() => import('./pages/IcmReportPage'), 'IcmReportPage');
const WorkloadSummaryPage = lazyPage(() => import('./pages/WorkloadSummaryPage'), 'WorkloadSummaryPage');
const IntakeReportPage = lazyPage(() => import('./pages/IntakeReportPage'), 'IntakeReportPage');
const KrCountsReportPage = lazyPage(() => import('./pages/KrCountsReportPage'), 'KrCountsReportPage');
const AccountActivityReportPage = lazyPage(() => import('./pages/AccountActivityReportPage'), 'AccountActivityReportPage');
const FedSnowReportPage = lazyPage(() => import('./pages/FedSnowReportPage'), 'FedSnowReportPage');
const PainMeterPage = lazyPage(() => import('./pages/PainMeterPage'), 'PainMeterPage');
const ReliefEffectivenessPage = lazyPage(() => import('./pages/ReliefEffectivenessPage'), 'ReliefEffectivenessPage');
const TriageAgingReportPage = lazyPage(() => import('./pages/TriageAgingReportPage'), 'TriageAgingReportPage');
const ExecutiveOverviewReportPage = lazyPage(() => import('./pages/ExecutiveOverviewReportPage'), 'ExecutiveOverviewReportPage');
const IntakeFunnelReportPage = lazyPage(() => import('./pages/IntakeFunnelReportPage'), 'IntakeFunnelReportPage');
const HotSpotReportPage = lazyPage(() => import('./pages/HotSpotReportPage'), 'HotSpotReportPage');
const CrossSurfaceHotSpotReportPage = lazyPage(() => import('./pages/CrossSurfaceHotSpotReportPage'), 'CrossSurfaceHotSpotReportPage');
const TriageLagTrendReportPage = lazyPage(() => import('./pages/TriageLagTrendReportPage'), 'TriageLagTrendReportPage');
const TriageTimingReportPage = lazyPage(() => import('./pages/TriageTimingReportPage'), 'TriageTimingReportPage');
const TemplateAdoptionReportPage = lazyPage(() => import('./pages/TemplateAdoptionReportPage'), 'TemplateAdoptionReportPage');
const OutcomeMixReportPage = lazyPage(() => import('./pages/OutcomeMixReportPage'), 'OutcomeMixReportPage');
const ApprovalPerformanceReportPage = lazyPage(() => import('./pages/ApprovalPerformanceReportPage'), 'ApprovalPerformanceReportPage');
const RejectionReasonsReportPage = lazyPage(() => import('./pages/RejectionReasonsReportPage'), 'RejectionReasonsReportPage');
const WorkflowHealthReportPage = lazyPage(() => import('./pages/WorkflowHealthReportPage'), 'WorkflowHealthReportPage');
const ExceptionExpiryReportPage = lazyPage(() => import('./pages/ExceptionExpiryReportPage'), 'ExceptionExpiryReportPage');
const DataQualityReportPage = lazyPage(() => import('./pages/DataQualityReportPage'), 'DataQualityReportPage');
const RoutingReportPage = lazyPage(() => import('./pages/RoutingReportPage'), 'RoutingReportPage');
const DeclinedDetailReportPage = lazyPage(() => import('./pages/DeclinedDetailReportPage'), 'DeclinedDetailReportPage');
const DeflectionEffectivenessReportPage = lazyPage(() => import('./pages/DeflectionEffectivenessReportPage'), 'DeflectionEffectivenessReportPage');
const ElevatedAccessInsightsReportPage = lazyPage(() => import('./pages/ElevatedAccessInsightsReportPage'), 'ElevatedAccessInsightsReportPage');
const AdminToolsPage = lazyPage(() => import('./pages/AdminToolsPage'), 'AdminToolsPage');
const ReportsPage = lazyPage(() => import('./pages/ReportsPage'), 'ReportsPage');
const ReportBucketPage = lazyPage(() => import('./pages/ReportBucketPage'), 'ReportBucketPage');
const ManagementToolsPage = lazyPage(() => import('./pages/ManagementToolsPage'), 'ManagementToolsPage');
const ChangeRadarDashboardPage = lazyPage(() => import('./pages/ChangeRadarDashboardPage'), 'ChangeRadarDashboardPage');
const ChangeRadarBaselinesPage = lazyPage(() => import('./pages/ChangeRadarBaselinesPage'), 'ChangeRadarBaselinesPage');
const ChangeRadarMonitoringPage = lazyPage(() => import('./pages/ChangeRadarMonitoringPage'), 'ChangeRadarMonitoringPage');
const PartnerGovernancePage = lazyPage(() => import('./pages/PartnerGovernancePage'), 'PartnerGovernancePage');
const IpAnchorMyAccountsPage = lazyPage(() => import('./pages/IpAnchorMyAccountsPage'), 'IpAnchorMyAccountsPage');
const IpAnchorAccountPage = lazyPage(() => import('./pages/IpAnchorAccountPage'), 'IpAnchorAccountPage');
const IpAnchorAdminPage = lazyPage(() => import('./pages/IpAnchorAdminPage'), 'IpAnchorAdminPage');
const ChangeRadarGoNoGoPage = lazyPage(() => import('./pages/ChangeRadarGoNoGoPage'), 'ChangeRadarGoNoGoPage');
const ConditionalAccessReadinessPage = lazyPage(
  () => import('./pages/ConditionalAccessReadinessPage'),
  'ConditionalAccessReadinessPage',
);
const ConditionalAccessInvestigationPage = lazyPage(
  () => import('./pages/ConditionalAccessInvestigationPage'),
  'ConditionalAccessInvestigationPage',
);
const MsApproveOpsHealthPage = lazyPage(() => import('./pages/MsApproveOpsHealthPage'), 'MsApproveOpsHealthPage');
const AppConsentPage = lazyPage(() => import('./pages/AppConsentPage'), 'AppConsentPage');

const STORAGE_KEY_COLOR_MODE = 'askiam-color-mode';

/** Invisible component that tracks page views on route changes */
const PageTracker: React.FC = () => { usePageTracking(); return null; };

const AppContent: React.FC = () => {
  const { isLoading, isAuthenticated } = useAuth();

  if (isLoading || !isAuthenticated) {
    return (
      <Box role="status" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 2 }}>
        <CircularProgress aria-label="Signing in" />
        <Typography>Signing in...</Typography>
      </Box>
    );
  }

  return (
    <>
      <PageTracker />
      <ErrorBoundary>
        <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/my-requests" element={<MyRequestsPage />} />
          <Route
            path="/app-consent"
            element={
              <FeatureRoute flag="Requests.AdminConsent.Production">
                <AppConsentPage />
              </FeatureRoute>
            }
          />
          <Route path="/requests" element={<SelectRequestTypePage />} />
          <Route path="/exceptions" element={<ExceptionCentralPage />} />
          <Route
            path="/attestations"
            element={
              <FeatureRoute flag="IdentityTools.AttestationCentral">
                <AttestationCentralPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/attestations/uami"
            element={
              <FeatureRoute flag="IdentityTools.AttestationCentral.UamiDashboard">
                <UamiAttestationDashboardPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/attestations/uami/admin"
            element={
              <FeatureRoute flag="IdentityTools.AttestationCentral.UamiDashboard">
                <UamiCampaignAdminPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/attestations/custom-roles"
            element={
              <FeatureRoute flag="IdentityTools.AttestationCentral.CustomRoleDashboard">
                <CustomRoleAttestationDashboardPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/attestations/app"
            element={
              <FeatureRoute flag="IdentityTools.AttestationCentral">
                <AppAttestationMockPage />
              </FeatureRoute>
            }
          />
          <Route path="/user-tools" element={<UserToolsPage />} />
          <Route path="/admin-tools" element={<AdminToolsPage />} />
          <Route
            path="/partner-governance"
            element={
              <FeatureRoute flag="IdentityTools.PartnerGovernance">
                <PartnerGovernancePage />
              </FeatureRoute>
            }
          />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/reports/category/:bucketKey" element={<ReportBucketPage />} />
          <Route path="/management-tools" element={<ManagementToolsPage />} />
          <Route path="/boards/:kind" element={<FeedbackBoardPage />} />
          <Route
            path="/harvest"
            element={
              <FeatureRoute flag="IdentityTools.Harvest">
                <HarvestManagementPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/onboarding-buddy"
            element={
              <FeatureRoute flag="IdentityTools.OnboardingBuddy">
                <OnboardingBuddyPage />
              </FeatureRoute>
            }
          />
          <Route path="/work-items/onboard" element={<WorkItemOnboardTeamPage />} />
          <Route path="/work-items/routing" element={<WorkItemRoutingAdminPage />} />
          <Route path="/work-items/capability-matrix" element={<CapabilityMatrixAdminPage />} />
          <Route path="/work-items/deflection-catalog" element={<DeflectionCatalogPage />} />
          <Route path="/work-items/taxonomy-mapping" element={<FeatureRoute flag="Intake.TaxonomyMapping"><TaxonomyMappingPage /></FeatureRoute>} />
          <Route path="/work-items/teams" element={<WorkItemManageTeamPage />} />
          <Route path="/work-requests/new" element={<WorkRequestSubmitPage />} />
          <Route path="/work-requests/triage" element={<TeamBacklogPage />} />
          <Route path="/work-requests/:id/artifact" element={<WorkRequestArtifactPage />} />
          <Route path="/work-requests/:id" element={<WorkRequestDetailPage />} />
          <Route path="/requests/new/:requestType" element={<NewRequestPage />} />
          <Route path="/requests/submitted" element={<RequestSubmittedPage />} />
          <Route path="/requests/:id" element={<RequestDetailPage />} />
          <Route path="/manage-requests" element={<ManageRequestsPage />} />
          <Route path="/manage-exceptions" element={<ManageExceptionsPage />} />
          {/*
            Gated routes stay registered and decide inside FeatureRoute. The server enforces each
            flag independently, so this is a visibility decision, not a security one.
          */}
          <Route
            path="/bulk-upload-exceptions"
            element={
              <FeatureRoute flag="Requests.Exceptions.BulkUpload">
                <BulkUploadExceptionsPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/exception-report"
            element={
              <FeatureRoute flag="Reports.Exceptions">
                <ExceptionReportPage />
              </FeatureRoute>
            }
          />
          <Route path="/claims" element={<ClaimsPage />} />
          <Route path="/clearance-evaluate" element={<ClearanceEvaluatePage />} />
          <Route path="/whoami" element={<WhoAmIPage />} />
          <Route
            path="/inactive-users"
            element={
              <FeatureRoute flag="Reports.InactiveUsers">
                <InactiveUsersPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/ipanchor"
            element={
              <FeatureRoute flag="IdentityTools.IpAnchor">
                <IpAnchorMyAccountsPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/ipanchor/accounts/:objectId"
            element={
              <FeatureRoute flag="IdentityTools.IpAnchor">
                <IpAnchorAccountPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/ipanchor/admin"
            element={
              <FeatureRoute flag="IdentityTools.IpAnchor">
                <IpAnchorAdminPage />
              </FeatureRoute>
            }
          />
          <Route path="/ip-anchor/*" element={<LegacyIpAnchorRedirect />} />
          <Route
            path="/icm-report"
            element={
              <FeatureRoute flag="Reports.Icm">
                <IcmReportPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports/workload-summary"
            element={
              <FeatureRoute flag="Reports.KrCounts">
                <WorkloadSummaryPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports/intake"
            element={
              <FeatureRoute flag="Reports.KrCounts">
                <IntakeReportPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports/kr-counts"
            element={
              <FeatureRoute flag="Reports.KrCounts">
                <KrCountsReportPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports/account-activity"
            element={
              <FeatureRoute flag="Reports.AccountActivity">
                <AccountActivityReportPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports/fed-snow"
            element={
              <FeatureRoute flag="Reports.FedSnow">
                <FedSnowReportPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports/pain-meter"
            element={
              <FeatureRoute flag="Reports.PainMeter">
                <PainMeterPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports/relief-effectiveness"
            element={
              <FeatureRoute flag="Reports.ReliefEffectiveness">
                <ReliefEffectivenessPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports/triage-aging"
            element={
              <FeatureRoute flag="Reports.TriageAging">
                <TriageAgingReportPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports/overview"
            element={
              <FeatureRoute flag="Reports.ExecutiveOverview">
                <ExecutiveOverviewReportPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports/intake-funnel"
            element={
              <FeatureRoute flag="Reports.IntakeFunnel">
                <IntakeFunnelReportPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports/hot-spots"
            element={
              <FeatureRoute flag="Reports.HotSpots">
                <HotSpotReportPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports/cross-surface-hot-spots"
            element={
              <FeatureRoute flag="Reports.CrossSurfaceHotSpots">
                <CrossSurfaceHotSpotReportPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports/triage-lag"
            element={
              <FeatureRoute flag="Reports.TriageLagTrend">
                <TriageLagTrendReportPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports/triage-timing"
            element={
              <FeatureRoute flag="Reports.TriageTiming">
                <TriageTimingReportPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports/template-adoption"
            element={
              <FeatureRoute flag="Reports.TemplateAdoption">
                <TemplateAdoptionReportPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports/outcome-mix"
            element={
              <FeatureRoute flag="Reports.OutcomeMix">
                <OutcomeMixReportPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports/approval-performance"
            element={
              <FeatureRoute flag="Reports.ApprovalPerformance">
                <ApprovalPerformanceReportPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports/rejection-reasons"
            element={
              <FeatureRoute flag="Reports.RejectionReasons">
                <RejectionReasonsReportPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports/workflow-health"
            element={
              <FeatureRoute flag="Reports.WorkflowHealth">
                <WorkflowHealthReportPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports/exception-expiry"
            element={
              <FeatureRoute flag="Reports.ExceptionExpiry">
                <ExceptionExpiryReportPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports/data-quality"
            element={
              <FeatureRoute flag="Reports.DataQuality">
                <DataQualityReportPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports/routing"
            element={
              <FeatureRoute flag="Reports.Routing">
                <RoutingReportPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports/declined-detail"
            element={
              <FeatureRoute flag="Reports.DeclinedDetail">
                <DeclinedDetailReportPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports/deflection"
            element={
              <FeatureRoute flag="Reports.DeflectionEffectiveness">
                <DeflectionEffectivenessReportPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/reports/elevated-access"
            element={
              <FeatureRoute flag="Reports.ElevatedAccess">
                <ElevatedAccessInsightsReportPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <FeatureRoute flag="Reports.Analytics">
                <AnalyticsDashboardPage />
              </FeatureRoute>
            }
          />
          <Route path="/guestpass" element={<GuestPassNewUiPage />} />
          <Route path="/guestpass-old" element={<GuestPassPage />} />
          <Route path="/guestpass-new-ui" element={<Navigate to="/guestpass" replace />} />
          <Route path="/guest-pass" element={<Navigate to="/guestpass" replace />} />
          <Route path="/hr-account-lookup" element={<HrAccountLookupPage />} />
          <Route path="/hr-account-lookup/faq" element={<HrAccountLookupPage />} />
          <Route path="/hr-account-lookup/logs/all" element={<HrAccountLookupPage />} />
          <Route path="/coreid-hr-account-lookup" element={
            <FeatureRoute flag="IdentityTools.CoreIdHrAccountLookup">
              <CoreIdHrAccountLookupPage />
            </FeatureRoute>
          } />
          <Route
            path="/pim-sync"
            element={
              <FeatureRoute flag="IdentityTools.PimSync.Management">
                <PimSyncManagementPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/totp-manager"
            element={
              <FeatureRoute flag="IdentityTools.Totp.Management">
                <TotpManagementPage />
              </FeatureRoute>
            }
          />
          <Route path="/contributors" element={<ContributorsPage />} />
          <Route
            path="/iam-agent-assistant"
            element={
              <FeatureRoute flag="Operations.SreAgentRead">
                <HelpdeskIamAgentAssistantPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/clearance"
            element={
              <FeatureRoute flag="IdentityTools.Clearance">
                <ClearanceLayout />
              </FeatureRoute>
            }
          >
            <Route index element={<Navigate to="/clearance/flow" replace />} />
            <Route path="flow" element={<ClearanceVisualizationPage />} />
            <Route path="applications" element={<ClearanceApplicationsPage />} />
            <Route path="operation-contexts" element={<ClearanceOperationContextsPage />} />
            <Route path="services" element={<ClearanceServicesPage />} />
            <Route path="tenants" element={<ClearanceTenantsPage />} />
            <Route path="evaluate" element={<ClearanceEvaluatePage />} />
            <Route path="visualization" element={<ClearanceVisualizationPage />} />
          </Route>
          <Route
            path="/helpdesk"
            element={
              <FeatureRoute flag="IdentityTools.Helpdesk">
                <HelpdeskLayout />
              </FeatureRoute>
            }
          >
            <Route index element={<Navigate to="/helpdesk/cafe" replace />} />
            <Route path="cafe" element={<HelpdeskCafePage />} />
            <Route path="risk" element={<HelpdeskRiskPage />} />
            <Route path="falcon" element={<HelpdeskFalconPage />} />
            <Route path="phish" element={<HelpdeskPhishPage />} />
            <Route
              path="iam-agent-assistant"
              element={<Navigate to="/iam-agent-assistant" replace />}
            />
          </Route>
          <Route path="/admin/diagnostics" element={<FicDiagnosticsPage />} />
          <Route
            path="/admin/operations-center/analytics"
            element={<Navigate to="/reports/approval-performance#decision-history" replace />}
          />
          <Route
            path="/admin/operations-center"
            element={
              <FeatureRoute flag="IdentityTools.MsApproveOperations">
                <MsApproveOperationsLayout />
              </FeatureRoute>
            }
          >
            <Route index element={<Navigate to="/admin/operations-center/health" replace />} />
            <Route path="health" element={<MsApproveOpsHealthPage />} />
          </Route>
          <Route
            path="/change-radar"
            element={
              <FeatureRoute flag="IdentityTools.ChangeRadar">
                <ChangeRadarLayout />
              </FeatureRoute>
            }
          >
            <Route index element={<Navigate to="/change-radar/dashboard" replace />} />
            <Route path="dashboard" element={<ChangeRadarDashboardPage />} />
            <Route path="baselines" element={<ChangeRadarBaselinesPage />} />
            <Route path="monitoring" element={<ChangeRadarMonitoringPage />} />
            <Route path="go-no-go" element={<ChangeRadarGoNoGoPage />} />
          </Route>
          <Route
            path="/conditional-access-readiness"
            element={
              <FeatureRoute flag="IdentityTools.ConditionalAccessReadiness">
                <ConditionalAccessReadinessPage />
              </FeatureRoute>
            }
          />
          <Route
            path="/conditional-access-investigation"
            element={
              <FeatureRoute flag="IdentityTools.ConditionalAccessInvestigation">
                <ConditionalAccessInvestigationPage />
              </FeatureRoute>
            }
          />
        </Route>
        </Routes>
      </ErrorBoundary>
    </>
  );
};

const App: React.FC = () => {
  const [mode, setMode] = React.useState<PaletteMode>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_COLOR_MODE);
    return saved === 'dark' || saved === 'light' ? saved : 'light';
  });
  const theme = React.useMemo(() => createAppTheme(mode), [mode]);

  const colorModeValue = React.useMemo(
    () => ({
      mode,
      toggleColorMode: () =>
        setMode((prev) => {
          const next = prev === 'light' ? 'dark' : 'light';
          localStorage.setItem(STORAGE_KEY_COLOR_MODE, next);
          return next;
        }),
    }),
    [mode],
  );

  return (
    <ColorModeContext.Provider value={colorModeValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <Routes>
            <Route path="/sign-in" element={<SignInPage />} />
            <Route path="*" element={
              <AuthProvider>
                <EnvironmentProvider>
                  <TeamAccessProvider>
                    <AssistantProvider>
                      <AppContent />
                    </AssistantProvider>
                  </TeamAccessProvider>
                </EnvironmentProvider>
              </AuthProvider>
            } />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export default App;
